import { Dispatch, Fragment, SetStateAction, useMemo, useState } from "react";

import useModule from "../../../hooks/useModule";
import ModuleFieldsPicker from "../../TableLayouts/ModuleFieldsPicker";
import { RelatedRecords } from "../../../types/GlobalInterfaces";
import { Field } from "../../../types/TenantInterfaces";
import CollapseItem from "../../FormBuilder/components/CollapseItem";

interface SharedRecordFields {
  fields: string[];
  setFields: Dispatch<SetStateAction<string[]>>;
  moduleAPIName: string;
  relatedRecords: RelatedRecords;
  setRelatedRecords: Dispatch<SetStateAction<RelatedRecords>>;
  RelatedRecordsHeader: JSX.Element;
  useRelatedRecords: boolean;
}

const SharedRecordFieldsTab = ({
  moduleAPIName,
  fields,
  setFields,
  relatedRecords,
  setRelatedRecords,
  RelatedRecordsHeader,
  useRelatedRecords,
}: SharedRecordFields) => {
  const { module } = useModule(moduleAPIName, true);
  const [error, setError] = useState<{ message: string } | null>(null);

  const availableFields = useMemo(() => {
    return module?.Fields ?? [];
  }, [moduleAPIName, module?.Fields]);

  const referenceFields = useMemo(() => {
    return module?.Fields?.flatMap((f) => {
      if (!["SingleReference", "MultiReference"].includes(f?.FieldType))
        return [];

      if (!fields.includes(f.APIName)) return [];

      if (
        (typeof f?.SourcesList === "string" && f?.SourcesList === "Any") ||
        (Array.isArray(f?.SourcesList) && f?.SourcesList?.length > 1)
      )
        return [];

      if (!!typeof f?.SourcesList) return [f];
      else return [];
    });
  }, [moduleAPIName, fields]);

  if (!module) return null;

  return (
    <>
      <ModuleFieldsPicker
        values={fields}
        availableFields={availableFields}
        module={module}
        setError={setError}
        setValues={setFields}
        onChange={setFields}
        placeholder="Select fields to share."
      />
      <SharedRelatedRecords
        relatedRecords={relatedRecords}
        setRelatedRecords={setRelatedRecords}
        referenceFields={referenceFields || []}
        RelatedRecordsHeader={RelatedRecordsHeader}
        useRelatedRecords={useRelatedRecords}
      />
    </>
  );
};

const SharedRelatedRecords = ({
  relatedRecords,
  setRelatedRecords,
  referenceFields,
  RelatedRecordsHeader,
  useRelatedRecords,
}: {
  relatedRecords: RelatedRecords;
  setRelatedRecords: Dispatch<SetStateAction<RelatedRecords>>;
  referenceFields: Field[];
  RelatedRecordsHeader: JSX.Element;
  useRelatedRecords: boolean;
}) => {
  const { getModule } = useModule();

  const rrd = useMemo(() => {
    if (!referenceFields?.length) return [];
    let rrInfo = [];

    for (let referenceField of referenceFields) {
      let sourceModule =
        typeof referenceField?.SourcesList === "string"
          ? referenceField?.SourcesList
          : referenceField?.SourcesList?.[0] ?? "";

      if (!sourceModule) continue;

      sourceModule = sourceModule.replace("Tenant.Modules.", "");

      const module = getModule(sourceModule, false, true);

      if (!module) continue;

      const relatedRecordFields = relatedRecords[referenceField?.APIName] ?? [];

      rrInfo.push({
        Title: referenceField.Name,
        ApiName: referenceField?.APIName,
        Module: module,
        Fields: relatedRecordFields,
      });
    }

    return rrInfo;
  }, [relatedRecords, referenceFields]);

  return (
    <>
      {RelatedRecordsHeader}
      {useRelatedRecords &&
        rrd.map((rr) => {
          return (
            <Fragment key={rr.ApiName}>
              <CollapseItem
                title={rr.Title}
                children={
                  <>
                    <ModuleFieldsPicker
                      values={rr.Fields}
                      availableFields={rr.Module?.Fields}
                      module={rr.Module}
                      setError={() => {}}
                      setValues={(fields) => {
                        //@ts-ignore
                        setRelatedRecords((prev) => ({
                          ...prev,
                          [rr.ApiName]: fields,
                        }));
                      }}
                      onChange={(fields) => {
                        setRelatedRecords((prev) => ({
                          ...prev,
                          [rr.ApiName]: fields,
                        }));
                      }}
                      placeholder="Select fields to share."
                    />
                  </>
                }
              />
            </Fragment>
          );
        })}
    </>
  );
};

export default SharedRecordFieldsTab;
